import { useEffect, useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material'
import { Lightbulb, Close } from '@mui/icons-material'
import { AdventureDto } from '@api/types/adventure'
import { SessionStatus, SessionNodeStatus, UserRelationType } from '@api/types/session'
import { WorkflowNodeType } from '../../types/workflow.types'
import { useSessionStore } from '../../stores/sessionStore'
import { useAuthStore } from '@store/authStore'
import {
  useActiveSessionForAdventure,
  useStartSession,
  useResumeSession,
  useProcessSession,
  useSessionState,
  useAbandonSession,
} from '../../hooks/useSession'
import { useElapsedTimer } from '../../hooks/useElapsedTimer'
import { useSessionLocationTracking } from '../../hooks/useSessionLocationTracking'
import PlayerHeader from './PlayerHeader'
import { useClueStatus, useRequestClue } from '../../hooks/useSessionClues'
import CooldownIndicator from './CooldownIndicator'
import NodeRenderer from './nodes/NodeRenderer'
import ModeratorApprovalWait from './nodes/ModeratorApprovalWait'
import ExitNode from './nodes/ExitNode'
import JournalNotebook from './journal/JournalNotebook'
import StatsBar from './StatsBar'
import VotingPanel from './voting/VotingPanel'
import SessionInvitePanel from './SessionInvitePanel'
import SessionSettingsPanel from './SessionSettingsPanel'
import SessionLobby from './SessionLobby'
import GroupChatBanner from './GroupChatBanner'
import { useSessionSignalR } from '../../hooks/useSessionSignalR'

// Node types that auto-advance (no user input required)
const AUTO_ADVANCE_NODE_TYPES = [
  WorkflowNodeType.Start,
  WorkflowNodeType.FormattedMessage,
  WorkflowNodeType.TextClue,
  WorkflowNodeType.PoiLocation,
  WorkflowNodeType.AudioPlayer,
  WorkflowNodeType.VideoPlayer,
  WorkflowNodeType.ImageDisplay,
  WorkflowNodeType.TimeModifier,
]

// Auto-advance delay in milliseconds (time to show content before advancing)
const AUTO_ADVANCE_DELAY = 2500

interface AdventurePlayerProps {
  adventureId: string
  adventure: AdventureDto
}

export default function AdventurePlayer({ adventureId, adventure }: AdventurePlayerProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const store = useSessionStore()
  const user = useAuthStore((s) => s.user)
  const [invitePanelOpen, setInvitePanelOpen] = useState(false)
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)
  const [showLobby, setShowLobby] = useState(false)
  const [chatOpenTrigger, setChatOpenTrigger] = useState(0)
  // Check if user can access debug panel
  // Allowed: System Admin, System Moderator, Adventure Creator, Maintainer, Tester, Adventure Moderator
  const canDebug = useMemo(() => {
    // System-level roles
    const isSystemAdmin = user?.roles?.includes('Admin') ?? false
    const isSystemModerator = user?.roles?.includes('Moderator') ?? false
    if (isSystemAdmin || isSystemModerator) return true

    // Adventure-level roles via userRelationType bitmask
    const relation = adventure.userRelationType ?? 0
    const isMaintainer = (relation & UserRelationType.Maintainer) !== 0
    const isTester = (relation & UserRelationType.Tester) !== 0
    const isAdventureModerator = (relation & UserRelationType.Moderator) !== 0

    // Check if user is the creator
    const isCreator = user?.userId === adventure.createdBy

    return isCreator || isMaintainer || isTester || isAdventureModerator
  }, [user, adventure])

  // Check if session ID was passed via navigation state (from join/solo)
  const passedSessionId = (location.state as { sessionId?: string } | null)?.sessionId

  // Reset session store on every mount to clear stale data from previous sessions.
  // Use a layout-style reset to ensure it happens before initSession fires.
  useEffect(() => {
    store.reset()
    initializingRef.current = false
    return () => {
      // Also clear on unmount so remounting the same adventureId starts fresh
      initializingRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adventureId])

  // Ref to prevent duplicate initialization calls
  const initializingRef = useRef(false)

  // Clue dialog state
  const [clueConfirmOpen, setClueConfirmOpen] = useState(false)
  const requestClue = useRequestClue(store.sessionId)
  const { data: clueStatus } = useClueStatus(store.sessionId, !showLobby)

  // Sync clue status polling into session store
  useEffect(() => {
    if (clueStatus) {
      store.setClueState(clueStatus.hasAvailableClues, clueStatus.cluesUsed, clueStatus.totalPenaltySeconds)
    }
  }, [clueStatus])

  // Check for existing active session (only if no session ID was passed)
  const {
    data: activeSession,
    isLoading: checkingExisting,
    isFetched: sessionCheckComplete,
  } = useActiveSessionForAdventure(adventureId)

  const hasActiveSession = activeSession !== null && activeSession !== undefined
  const noSessionFound = sessionCheckComplete && !hasActiveSession && !passedSessionId

  // Mutations
  const startSession = useStartSession()
  const resumeSession = useResumeSession()
  const processSession = useProcessSession()
  const abandonSession = useAbandonSession()

  // Track how long we've been stuck loading so we can offer an escape hatch
  const [loadingTooLong, setLoadingTooLong] = useState(false)
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    const isStuck = checkingExisting || store.isLoading || (!store.sessionId || !store.currentNode)
    if (isStuck && !store.error) {
      loadingTimerRef.current = setTimeout(() => setLoadingTooLong(true), 8000)
    } else {
      setLoadingTooLong(false)
    }
    return () => { if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current) }
  }, [checkingExisting, store.isLoading, store.sessionId, store.currentNode, store.error])

  const handleAbandonAndRetry = useCallback(async () => {
    // Try to abandon any active session for this adventure, then redirect to detail page
    try {
      if (store.sessionId) {
        await abandonSession.mutateAsync(store.sessionId)
      } else if (activeSession?.id) {
        const { sessionsApi } = await import('@api/endpoints/sessions')
        await sessionsApi.abandonSession(activeSession.id)
      }
    } catch {
      // Abandon failed — still navigate away so user isn't stuck
    }
    store.reset()
    navigate(`/adventures/${adventureId}?details=true`, { replace: true })
  }, [store, abandonSession, activeSession, adventureId, navigate])

  // Poll for state updates (every 5 seconds if session is active or in lobby)
  const shouldPoll = store.sessionId && (
    store.status === SessionStatus.InProgress ||
    (showLobby && store.status === SessionStatus.NotStarted)
  )

  useSessionState(store.sessionId, {
    pollInterval: shouldPoll ? 5000 : undefined,
    enabled: !!store.sessionId && store.status !== SessionStatus.Completed,
  })

  // Real-time session updates via SignalR (voting progress, voting resolved)
  useSessionSignalR(store.isGroup ? store.sessionId : null)

  // Elapsed time tracking - uses server's effectiveDuration and ticks client-side
  const { elapsedSeconds } = useElapsedTimer({
    effectiveDuration: store.effectiveDuration,
    isActive: store.status === SessionStatus.InProgress,
    isTimerPaused: store.isTimerPaused,
  })

  // Location tracking - sends periodic location updates for condition evaluation
  useSessionLocationTracking({
    sessionId: store.sessionId,
    sessionStatus: store.status,
    enabled: true,
  })

  // Auto-transition from lobby to game when session starts
  useEffect(() => {
    if (showLobby && store.status === SessionStatus.InProgress) {
      setShowLobby(false)
    }
  }, [showLobby, store.status])

  // Initialize or resume session
  useEffect(() => {
    const initSession = async () => {
      // Don't initialize if already have a session loaded or already initializing
      if (store.sessionId || initializingRef.current) return

      // If session ID was passed via navigation state (from join/solo), use it directly
      if (passedSessionId) {
        initializingRef.current = true
        store.setLoading(true)
        try {
          const { sessionsApi } = await import('@api/endpoints/sessions')
          // Use getSession (not getSessionState) to get full DTO including votingSettings
          const state = await sessionsApi.getSession(passedSessionId)

          // If session is NotStarted, start it (solo) or show lobby (group)
          if (state.status === SessionStatus.NotStarted) {
            if (state.isGroup) {
              store.loadSession(state)
              store.setLoading(false)
              setShowLobby(true)
            } else {
              await startSession.mutateAsync(passedSessionId)
            }
          } else if (state.status === SessionStatus.Paused) {
            await resumeSession.mutateAsync(passedSessionId)
          } else {
            // Session already in progress or other state - just load it
            store.loadSession(state)
            store.setLoading(false)
          }
        } catch (err: any) {
          // Check if 404 - session may have been instantly completed (common for geocaches)
          if (err?.response?.status === 404 || err?.status === 404) {
            // Fetch adventure to check if it's completed
            try {
              const { adventuresApi } = await import('@api/endpoints/adventures')
              const advData = await adventuresApi.getAdventure(adventureId)
              if (advData.isCompleted) {
                // Adventure is already completed - redirect to detail page
                store.setLoading(false)
                navigate(`/adventures/${adventureId}`, {
                  replace: true,
                  state: { justCompleted: true }
                })
                initializingRef.current = false
                return
              }
            } catch {
              // Couldn't fetch adventure, fall through to error
            }
          }
          console.error('Failed to load session:', err)
          store.setLoading(false)
          store.setError('Failed to load session')
        } finally {
          initializingRef.current = false
        }
        return
      }

      // Wait for existing session check to complete
      if (checkingExisting) return

      if (hasActiveSession && activeSession) {
        initializingRef.current = true
        // Resume existing session
        store.setLoading(true)
        try {
          if (activeSession.status === SessionStatus.Paused) {
            await resumeSession.mutateAsync(activeSession.id)
          } else if (activeSession.status === SessionStatus.NotStarted) {
            if (activeSession.isGroup) {
              // Use getSession (not getSessionState) to get full DTO including votingSettings
              const { sessionsApi: api } = await import('@api/endpoints/sessions')
              const state = await api.getSession(activeSession.id)
              store.loadSession(state)
              store.setLoading(false)
              setShowLobby(true)
            } else {
              await startSession.mutateAsync(activeSession.id)
            }
          } else {
            // Just fetch the state for InProgress sessions
            const { sessionsApi } = await import('@api/endpoints/sessions')
            const state = await sessionsApi.getSessionState(activeSession.id)
            store.loadSession(state)
            store.setLoading(false)
          }
        } catch (err) {
          console.error('Failed to resume session:', err)
          store.setLoading(false)
          store.setError('Failed to resume session')
        } finally {
          initializingRef.current = false
        }
      }
      // Note: noSessionFound case is handled by separate useEffect below
    }

    initSession()
  }, [adventureId, activeSession, hasActiveSession, checkingExisting, store.sessionId, passedSessionId])

  // Redirect to description page when no active session is found
  useEffect(() => {
    if (noSessionFound) {
      // Navigate back to description page (without ?play=true)
      navigate(`/adventures/${adventureId}`, { replace: true })
    }
  }, [noSessionFound, adventureId, navigate])

  // Check if on cooldown
  const isOnCooldown = store.currentNodeStatus === SessionNodeStatus.OnCooldown

  // Auto-advance logic for auto-advance node types
  useEffect(() => {
    if (
      store.sessionId &&
      store.currentNode &&
      !store.requiresInput &&
      !store.isProcessing &&
      !store.isVotingActive &&
      !isOnCooldown &&
      store.status === SessionStatus.InProgress
    ) {
      const nodeType = store.currentNode.nodeType as WorkflowNodeType
      if (AUTO_ADVANCE_NODE_TYPES.includes(nodeType)) {
        const timer = setTimeout(() => {
          processSession.mutate(store.sessionId!)
        }, AUTO_ADVANCE_DELAY)
        return () => clearTimeout(timer)
      }
    }
  }, [
    store.sessionId,
    store.currentNode?.id,
    store.requiresInput,
    store.isProcessing,
    store.isVotingActive,
    store.status,
    isOnCooldown,
  ])

  // Manual continue handler (backup for auto-advance)
  const handleContinue = useCallback(() => {
    if (store.sessionId && !store.isProcessing) {
      processSession.mutate(store.sessionId)
    }
  }, [store.sessionId, store.isProcessing])

  // Handle abandon/back navigation
  const handleBack = useCallback(() => {
    store.reset()
    navigate('/my-adventures')
  }, [navigate, store])

  const handleRequestClue = useCallback(() => {
    setClueConfirmOpen(true)
  }, [])

  const handleConfirmClue = useCallback(async () => {
    setClueConfirmOpen(false)
    try {
      await requestClue.mutateAsync()
    } catch {
      // Error handled by mutation
    }
  }, [requestClue])

  // Loading state
  if (checkingExisting || store.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          {checkingExisting ? 'Checking for existing session...' : 'Loading adventure...'}
        </Typography>
        {loadingTooLong && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              This is taking longer than expected.
            </Typography>
            <Button variant="outlined" color="warning" size="small" onClick={handleAbandonAndRetry}>
              Abandon Session &amp; Retry
            </Button>
          </Box>
        )}
      </Box>
    )
  }

  // Error state
  if (store.error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {store.error}
        </Alert>
        <Button variant="outlined" color="warning" onClick={handleAbandonAndRetry}>
          Abandon Session &amp; Return
        </Button>
      </Box>
    )
  }

  // Redirecting to description page (no active session found)
  if (noSessionFound) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          No active session found. Returning to adventure...
        </Typography>
      </Box>
    )
  }

  // Show lobby for group sessions that haven't started
  if (showLobby && store.sessionId) {
    return (
      <SessionLobby
        sessionId={store.sessionId}
        adventureTitle={adventure.title}
        isOwner={store.isSessionOwner}
        conversationId={store.conversationId}
        onStarted={() => setShowLobby(false)}
        onLeft={() => {
          store.reset()
          navigate(`/adventures/${adventureId}`, { replace: true })
        }}
      />
    )
  }

  // No session yet (still initializing)
  if (!store.sessionId || !store.currentNode) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Starting adventure...
        </Typography>
        {loadingTooLong && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Unable to start the adventure. The session may be in a bad state.
            </Typography>
            <Button variant="outlined" color="warning" size="small" onClick={handleAbandonAndRetry}>
              Abandon Session &amp; Retry
            </Button>
          </Box>
        )}
      </Box>
    )
  }

  // Terminal state — show completion summary screen
  const isTerminalState = store.status === SessionStatus.Completed ||
    store.status === SessionStatus.Failed ||
    store.status === SessionStatus.Abandoned
  const isExitNodeType = [
    WorkflowNodeType.Exit,
    WorkflowNodeType.ExitSuccess,
    WorkflowNodeType.ExitFailed,
    WorkflowNodeType.ExitAbandoned,
  ].includes(store.currentNode.nodeType as WorkflowNodeType)

  if (isTerminalState || isExitNodeType) {
    // For abandon flow, currentNode may not be an exit type — create synthetic node
    const exitNode = isExitNodeType ? store.currentNode : {
      id: 'exit-summary',
      nodeType: store.status === SessionStatus.Completed ? WorkflowNodeType.ExitSuccess
        : store.status === SessionStatus.Failed ? WorkflowNodeType.ExitFailed
        : WorkflowNodeType.ExitAbandoned,
      label: store.currentNode.label || 'Exit',
      description: store.currentNode.description,
    }
    return <ExitNode node={exitNode} />
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Header */}
      <PlayerHeader
        title={adventure.title}
        elapsed={elapsedSeconds}
        isGroup={store.isGroup}
        participantCount={store.participantCount}
        canDebug={canDebug}
        onBack={handleBack}
        isTimerPaused={store.isTimerPaused}
        adventureType={adventure.tagsAdventureTypes?.[0] || undefined}
        creatorName={adventure.createdByName}
        isOwner={store.isSessionOwner}
        onInviteClick={() => setInvitePanelOpen(true)}
        conversationId={store.conversationId}
        onSettingsClick={store.isGroup ? () => setSettingsDialogOpen(true) : undefined}
      />

      {/* Proximity warning */}
      {store.proximityWarning && (
        <Alert severity="warning" sx={{ mx: 2, mt: 1, flexShrink: 0 }}>
          {store.proximityWarning}
        </Alert>
      )}

      {/* Stats bar */}
      <StatsBar />

      {/* Group chat banner */}
      {store.isGroup && store.conversationId && (
        <GroupChatBanner
          conversationId={store.conversationId}
          onPress={() => setChatOpenTrigger((c) => c + 1)}
        />
      )}

      {/* Session invite panel (for group owner) */}
      {store.isGroup && store.sessionId && (
        <SessionInvitePanel
          open={invitePanelOpen}
          onClose={() => setInvitePanelOpen(false)}
          sessionId={store.sessionId}
        />
      )}

      {/* Main content area - Full page notebook */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Journal Notebook - takes full width */}
        <JournalNotebook
          sessionId={store.sessionId}
          adventure={adventure}
          locations={store.revealedLocations}
          inputArea={
            store.requiresInput && store.pendingInput && store.currentNode ? (
              <NodeRenderer
                key={store.currentNode.id}
                node={store.currentNode}
                pendingInput={store.pendingInput}
                sessionId={store.sessionId}
              />
            ) : store.currentNode && store.currentNode.nodeType === WorkflowNodeType.ModeratorApproval && store.currentNodeStatus === SessionNodeStatus.AwaitingInput ? (
              <ModeratorApprovalWait
                key={store.currentNode.id}
                node={store.currentNode}
                pendingInput={{
                  nodeId: store.currentNode.id,
                  inputType: 'ModeratorApproval' as any,
                  required: false,
                  allowSkip: store.currentNode.allowSkip ?? false,
                  allowCancel: false,
                  skipButtonText: store.currentNode.skipButtonText,
                  cancelButtonText: store.currentNode.cancelButtonText,
                }}
              />
            ) : store.isVotingActive && store.voting ? (
              <VotingPanel
                voting={store.voting}
                sessionId={store.sessionId}
                questionText={store.votingQuestionText}
                challengeType={store.votingChallengeType}
                challengeData={store.votingChallengeData}
              />
            ) : undefined
          }
          hasAvailableClues={store.hasAvailableClues && !(clueStatus && clueStatus.maxClues > 0 && clueStatus.cluesUsed >= clueStatus.maxClues)}
          onRequestClue={handleRequestClue}
          hasPendingChallenge={(store.requiresInput && !!store.pendingInput) || (store.currentNode?.nodeType === WorkflowNodeType.ModeratorApproval && store.currentNodeStatus === SessionNodeStatus.AwaitingInput) || (store.isVotingActive && !!store.voting)}
          challengeInputType={store.pendingInput?.inputType || (store.currentNode?.nodeType === WorkflowNodeType.ModeratorApproval && store.currentNodeStatus === SessionNodeStatus.AwaitingInput ? 'ModeratorApproval' as any : undefined)}
          conversationId={store.conversationId}
          isGroup={store.isGroup}
          chatOpenTrigger={chatOpenTrigger}
        />
      </Box>

      {/* Session settings dialog */}
      {store.isGroup && store.sessionId && (
        <Dialog
          open={settingsDialogOpen}
          onClose={() => setSettingsDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
            Session Settings
            <IconButton size="small" onClick={() => setSettingsDialogOpen(false)}>
              <Close fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <SessionSettingsPanel
              sessionId={store.sessionId}
              isOwner={store.isSessionOwner}
              settings={store.votingSettings}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Clue confirmation dialog */}
      <Dialog open={clueConfirmOpen} onClose={() => setClueConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Lightbulb color="warning" />
          Use a Clue?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Are you sure you want to use a clue?
          </Typography>
          {clueStatus && clueStatus.penaltyPerClueSeconds > 0 && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 0.5 }}>
              Time penalty: +{clueStatus.penaltyPerClueSeconds} seconds
            </Typography>
          )}
          {clueStatus && clueStatus.maxClues > 0 && (
            <Typography variant="body2" color="text.secondary">
              Clues used: {clueStatus.cluesUsed} / {clueStatus.maxClues} ({clueStatus.maxClues - clueStatus.cluesUsed} remaining)
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClueConfirmOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleConfirmClue} variant="contained" color="warning">Use Clue</Button>
        </DialogActions>
      </Dialog>

      {/* Cooldown indicator */}
      {isOnCooldown && store.cooldownEndsAt && (
        <CooldownIndicator
          cooldownEndsAt={store.cooldownEndsAt}
          waitingReason={store.waitingReason}
          onCooldownEnd={() => {
            // Trigger a state refresh when cooldown ends
            if (store.sessionId) {
              processSession.mutate(store.sessionId)
            }
          }}
        />
      )}

    </Box>
  )
}
